import * as plantronicsSdk from './SDKs/plantronics-headset-sdk.js';
import { ChangesToHeadsetState } from './changes-to-headset-state.enum';
import { HeadsetDevice } from './headset-device';
import { AbstractSamwinHeadsetManufacturerService } from './headset-manufacturer-service.abstract';
import { HeadsetManufacturerType } from './headset-manufacturer-type';

import { Injectable } from '@angular/core';

import { Store } from '@ngxs/store';

import _ from 'lodash';
import { BehaviorSubject, Observable, combineLatest, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

@Injectable({
   providedIn: 'root',
})
export class PlantronicsService extends AbstractSamwinHeadsetManufacturerService {
   private readonly PLANTRONICS_HUB_ADDRESS: string = 'https://127.0.0.1:32018/Spokes';
   private readonly HUB_CONNECTION_POLL_RATE: number = 2000;
   private readonly SPOKES_EVENTS_POLL_RATE: number = 500;
   private readonly ERR_NO_HUB_RESPONSE: string = 'No response.  Server appears to be offline.';
   private readonly ERR_INVALID_SESSION_ID: string = 'Invalid session id';
   private readonly ERR_EMPTY_SESSION_ID: string = 'Empty session id';
   private readonly ERR_NO_SUPPORTED_DEVICES: string = 'There are no supported devices';
   private readonly API_CONSUMER: string = 'samwin Web Agent';

   private abstractInitDone: boolean;

   private initSDK$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   private killSdk$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   private changesToHeadsetState$: BehaviorSubject<ChangesToHeadsetState> = new BehaviorSubject<ChangesToHeadsetState>(null);

   private isMuted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   private pollRate$: BehaviorSubject<number> = new BehaviorSubject<number>(this.HUB_CONNECTION_POLL_RATE);
   private previousPollRate$: BehaviorSubject<number> = new BehaviorSubject<number>(this.HUB_CONNECTION_POLL_RATE);

   private spokes$: BehaviorSubject<plantronicsSdk.Spokes> = new BehaviorSubject<plantronicsSdk.Spokes>(null);

   private deviceAttachedAndConnectedWithHub$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   private isPluginRegistered$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   private isPollingEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

   constructor(store: Store) {
      super(store, HeadsetManufacturerType.PLANTRONICS);

      combineLatest([
         this.debouncedDevice$,
         this.debouncedPlugin$,
         this.deviceAttachedAndConnectedWithHub$.pipe(debounceTime(100), distinctUntilChanged()),
         this.isPluginRegistered$.pipe(debounceTime(100), distinctUntilChanged()),
         this.isPollingEnabled$.pipe(debounceTime(100), distinctUntilChanged()),
      ])
         .pipe(
            filter(([device, plugin, connected, registered, isEnabled]) => !_.isNil(device) && !_.isNil(plugin) && connected && registered && isEnabled),
            switchMap(([device, plugin]) =>
               this.pollRate$.pipe(switchMap((pollRate) => interval(pollRate))).pipe(tap(() => this.pollDeviceEventsOrReconnect(device, plugin)))
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.initSDK$
         .pipe(
            debounceTime(100),
            distinctUntilChanged(),
            filter((initiateSdk) => initiateSdk),
            tap(() => this.initSDK()),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.killSdk$
         .pipe(
            debounceTime(100),
            distinctUntilChanged(),
            filter((terminateSdk) => terminateSdk),
            tap(() => this.killSdk(this.device, this.plugin)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      combineLatest([this.debouncedDevice$, this.debouncedPlugin$])
         .pipe(
            filter(([device, plugin]) => !_.isNil(device) && !_.isNil(plugin)),
            tap(([device, plugin]) => this.connectToInitializedSdK(device, plugin)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.debouncedPlugin$
         .pipe(
            debounceTime(100),
            distinctUntilChanged(),
            filter((plugin) => !_.isNil(plugin)),
            switchMap((plugin) =>
               this.changesToHeadsetState$.pipe(
                  filter((change) => !_.isNil(change)),
                  tap((change) => this.executeHeadSetChange(change, plugin))
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   private get device$(): Observable<plantronicsSdk.Device> {
      return this.spokes$.pipe(map((spokes) => spokes?.Device));
   }

   private get plugin$(): Observable<plantronicsSdk.Plugin> {
      return this.spokes$.pipe(map((spokes) => spokes?.Plugin));
   }

   private get debouncedDevice$(): Observable<plantronicsSdk.Device> {
      return this.device$.pipe(debounceTime(100), distinctUntilChanged(), shareReplay(1));
   }

   private get debouncedPlugin$(): Observable<plantronicsSdk.Plugin> {
      return this.plugin$.pipe(debounceTime(100), distinctUntilChanged(), shareReplay(1));
   }

   private get deviceAttachedAndConnectedWithHub() {
      return this.deviceAttachedAndConnectedWithHub$.getValue();
   }

   private get isPluginRegistered() {
      return this.isPluginRegistered$.getValue();
   }

   private get pollRate(): plantronicsSdk.Device {
      return this.pollRate$.getValue();
   }

   private get previousPollRate(): plantronicsSdk.Device {
      return this.previousPollRate$.getValue();
   }

   private get spokes(): plantronicsSdk.Device {
      return this.spokes$.getValue();
   }

   private get device(): plantronicsSdk.Device {
      return this.spokes?.Device;
   }

   private get plugin(): plantronicsSdk.Plugin {
      return this.spokes?.Plugin;
   }

   private get isAttached(): boolean {
      return this.device?.isAttached ?? false;
   }

   private get isMuted(): boolean {
      return this.isMuted$.getValue();
   }

   public setPolling(): void {
      this.isPollingEnabled$.next(true);
   }

   public unsetPolling() {
      this.isPollingEnabled$.next(false);
   }

   public setIsMuted(isMuted: boolean): void {
      this.isMuted$.next(isMuted);
   }

   public publishDeviceList(device: plantronicsSdk.Device): void {
      const deviceList = new Array<HeadsetDevice>();

      if (_.isNil(device)) {
         console.warn('Plantronics.Service - No device found.');

         return;
      }

      deviceList.push({
         manufacturer: HeadsetManufacturerType.PLANTRONICS,
         id: device.Device_Id,
         name: device.Sess_Id,
      });
      console.debug('Plantronics.Service - Pushing Plantronics devices list: ', deviceList);
      this.attachedHeadset$.next(deviceList);
   }

   protected changeHeadsetState(change: ChangesToHeadsetState): void {
      this.changesToHeadsetState$.next(change);
   }

   protected executeHeadSetChange(change: ChangesToHeadsetState, plugin: plantronicsSdk.Plugin): void {
      if (_.isNil(plugin)) {
         console.warn('Plantronics.Service - No plugin found.');

         return;
      }

      if (this.deviceAttachedAndConnectedWithHub) {
         console.debug('Plantronics.Service - Changing Plantronics headset state to', change);
         switch (change) {
            case ChangesToHeadsetState.MuteHeadset:
               if (!this.isMuted) {
                  this.setIsMuted(true);
                  plugin.muteCall(this.API_CONSUMER, true, (result) => {
                     console.debug(`Plantronics.Service - Spokes response on muting headset: ${JSON.stringify(result)}`);
                  });
               }

               break;
            case ChangesToHeadsetState.UnmuteHeadset:
               if (this.isMuted) {
                  this.setIsMuted(false);
                  plugin.muteCall(this.API_CONSUMER, false, (result) => {
                     console.debug(`Plantronics.Service - Spokes response on unmuting headset: ${JSON.stringify(result)}`);
                  });
               }

               break;
            case ChangesToHeadsetState.OnHook:
               plugin.terminateCall(this.API_CONSUMER, this.getCallId(1), (result) => {
                  console.debug(`Plantronics.Service - Spokes response on terminating call: ${JSON.stringify(result)}`);
               });
               break;
            case ChangesToHeadsetState.OffHook:
               plugin.answerCall(this.API_CONSUMER, this.getCallId(1), (result) => {
                  console.debug(`Plantronics.Service - Spokes response on answering call event: ${JSON.stringify(result)}`);
               });
               break;
            case ChangesToHeadsetState.IncomingCall:
               plugin.incomingCall(this.API_CONSUMER, this.getCallId(1), this.getContact(''), 'Unknown', 'ToHeadset', (result) => {
                  console.debug(`Plantronics.Service - Spokes response on incoming call event: ${JSON.stringify(result)}`);
               });
               break;
            case ChangesToHeadsetState.OutgoingCall:
               plugin.outgoingCall(this.API_CONSUMER, this.getCallId(1), this.getContact(''), 'ToHeadset', (result) => {
                  console.debug(`Plantronics.Service - Spokes response on outgoing call event: ${JSON.stringify(result)}`);
               });
               break;
            case ChangesToHeadsetState.Hold:
               plugin.holdCall(this.API_CONSUMER, this.getCallId(1), (result) => {
                  console.debug(`Plantronics.Service - Spokes response on hodl call: ${JSON.stringify(result)}`);
               });
               break;
            case ChangesToHeadsetState.Resume:
               plugin.resumeCall(this.API_CONSUMER, this.getCallId(1), (result) => {
                  console.debug(`Plantronics.Service - Spokes response on resuming call event: ${JSON.stringify(result)}`);
               });
               break;
         }
      }
   }

   private connectToHub() {
      this.spokes$.next(new plantronicsSdk.Spokes(this.PLANTRONICS_HUB_ADDRESS));
   }

   private connectToInitializedSdK(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin) {
      if (_.isNil(device)) {
         console.warn('Plantronics.Service - No device found.');

         return;
      }

      device.deviceInfo((result) => {
         if (!result.isError) {
            if (result.Result != null) {
               // Log Spokes active device found (first in list returned, index 0)
               console.debug('Plantronics.Service - Device found = ' + result.Result.ProductName + ', id = ' + result.Result.Uid);
               this.deviceAttachedAndConnectedWithHub$.next(true);
               if (!this.abstractInitDone) {
                  this.abstractInitDone = true;
               }

               // attach to the device, provide a callback function for the result
               device.attach(result.Result.Uid, (session) => {
                  if (session.isError || !this.isAttached) {
                     console.debug('Plantronics.Service - Session Registration Error');
                     this.deviceAttachedAndConnectedWithHub$.next(false);
                     this.disconnectFromHub(device, plugin);
                  } else {
                     console.debug('Plantronics.Service - Session ID: ' + session.Result);
                     this.registerPlugin(device, plugin); // register a plugin (Plantronics API application session)
                  }
               });
            } else {
               console.debug('Plantronics.Service - Error: Device was null on connecting to Spokes. Is there a Plantronics device connected?');
               this.deviceAttachedAndConnectedWithHub$.next(false);
            }

            this.pollRate$.next(this.SPOKES_EVENTS_POLL_RATE); // waiting for device events now, force faster polling rate to start now (if applicable)
            if (this.previousPollRate === this.HUB_CONNECTION_POLL_RATE) {
               this.previousPollRate$.next(this.SPOKES_EVENTS_POLL_RATE);
            }
            // start polling the device and call state events...
            this.setPolling();
         } else {
            if (result.Err.Description === this.ERR_NO_SUPPORTED_DEVICES) {
               console.debug('Plantronics.Service - Please attach a Plantronics headset to the PC.');
            } else {
               this.unsetPolling(); // stop polling so the automatic reconnect doesn't happen
               this.attachedHeadset$.next([]);
               console.log(
                  'Error connecting to Plantronics Hub. (Have you installed and run Plantronics Hub from <a href="http://www.plantronics.com/software" target="_new">www.plantronics.com/software</a>, or ' +
                     'are you Firefox user and getting "Error connecting to Plantronics Hub."? If so visit this URL: <a href="https://127.0.0.1:32018/Spokes/DeviceServices/Info" target="_new">' +
                     'https://127.0.0.1:32018/Spokes/DeviceServices/Info</a> and click Advanced > Add Exception... to add a security exception to allow the connection.'
               );
            }
         }
      });
   }

   private pollDeviceEventsOrReconnect(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin) {
      if (this.previousPollRate !== this.pollRate) {
         this.unsetPolling();
         this.previousPollRate$.next(this.pollRate);
         this.setPolling();
      }

      if (_.isNil(device)) {
         console.warn('Plantronics.Service - No device found.');

         return;
      }

      if (!this.deviceAttachedAndConnectedWithHub || !this.isAttached) {
         this.connectToInitializedSdK(device, plugin);
         console.debug('Plantronics.Service - Polling for Hub / Device Re-Attach ');

         return;
      }

      this.pollDeviceEvents(device, plugin);

      if (_.isNil(plugin)) {
         console.warn('Plantronics.Service - No plugin found.');

         return;
      }

      this.pollCallEvents(device, plugin);
   }

   private pollDeviceEvents(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin) {
      // Poll for device events
      // informs us of a variety of Plantronics device state changes
      device.events((result) => {
         if (result.isError) {
            console.debug('Plantronics.Service - Error polling for device events: ' + result.Err.Description);
            if (result.Err.Description === this.ERR_NO_HUB_RESPONSE) {
               if (this.pollRate !== this.HUB_CONNECTION_POLL_RATE) {
                  this.pollRate$.next(this.HUB_CONNECTION_POLL_RATE);
                  console.debug('Plantronics.Service - changing POLL RATE to ' + this.pollRate);
               }
            }
            if (result.Err.Description === this.ERR_INVALID_SESSION_ID || result.Err.Description === this.ERR_EMPTY_SESSION_ID) {
               console.debug('Plantronics.Service - events - DEVICE DETACHED / SESSION INVALID');
               this.attachedHeadset$.next([]);
               this.deviceAttachedAndConnectedWithHub$.next(false);
               this.disconnectFromHub(device, plugin);
            }
         } else {
            // display list of events collected from REST service
            if (result.Result.length > 0) {
               for (const res of result.Result) {
                  console.debug('Plantronics.Service - Spokes event: ' + res.Event_Name);
                  this.consumeHeadsetSpokesEvent(res);
               }
            }
         }
      });
   }

   private pollCallEvents(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin) {
      // Poll for call state events (call control events)
      // informs us the calling state has changed, for example user as answered/terminated a call
      // using headset buttons - this event should be used in my app to actually connect/terminate the call!
      this.plugin.callEvents(this.API_CONSUMER, (result) => {
         if (result.isError) {
            console.debug('Plantronics.Service - Error polling for call events: ' + result.Err.Description);
            if (result.Err.Description === this.ERR_NO_HUB_RESPONSE) {
               this.pollRate$.next(this.HUB_CONNECTION_POLL_RATE);
               console.debug('Plantronics.Service - changing POLL RATE to ' + this.pollRate);
            }
            if (
               result.Err.Description === this.ERR_INVALID_SESSION_ID ||
               result.Err.Description === this.ERR_EMPTY_SESSION_ID ||
               result.Err.Description === this.ERR_NO_HUB_RESPONSE
            ) {
               console.debug('Plantronics.Service - callEvents - DEVICE DETACHED / SESSION INVALID');
               this.deviceAttachedAndConnectedWithHub$.next(false);
               this.disconnectFromHub(device, plugin);
            }
         } else {
            // display list of events collected from REST service
            if (result.Result.length > 0) {
               for (const res of result.Result) {
                  console.info('Plantronics.Service - Call events: ' + res.DeviceEvent);
                  this.consumeHeadsetCallEvent(res);
               }
            }
         }
      });
   }

   // Cleanup the Plantronics REST API
   private disconnectFromHub(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin) {
      console.debug('Plantronics.Service - Disconnecting from hub');
      this.unregisterPlugin(plugin);

      if (_.isNil(device)) {
         console.warn('Plantronics.Service - No device found to release.');

         return;
      }

      device?.release((result) => {
         if (!result.isError) {
            console.log('Plantronics.Service - Released device');
         } else {
            console.error('Plantronics.Service - Error releasing device');
         }
         console.log('Plantronics.Service - Disconnected from Spokes');
      });
   }

   // Unregister Spokes plugin (Plantronics API application session)
   // checked
   private unregisterPlugin(plugin: plantronicsSdk.Plugin) {
      if (_.isNil(plugin)) {
         console.warn('Plantronics.Service - No plugin found.');

         return;
      }

      if (!this.isPluginRegistered) {
         console.warn('Plantronics.Service - Plugin already unregistred.');

         return;
      }

      plugin?.unRegister(this.API_CONSUMER);
      this.isPluginRegistered$.next(false);
      console.log('Plantronics.Service - Plugin un-registered.');
   }

   // Register a Spokes Plugin (Plantronics API application session) to get access to Call Services, Device and Call events
   private registerPlugin(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin): void {
      if (_.isNil(plugin)) {
         console.warn('Plantronics.Service - No plugin found.');

         return;
      }

      if (this.isPluginRegistered) {
         console.warn('Plantronics.Service - Plugin already registred.');

         return;
      }

      plugin.register(this.API_CONSUMER, (result) => {
         if (!result.isError) {
            this.setPluginActive(device, plugin);
         } else {
            console.debug('Plantronics.Service - Info: registering plugin: ' + result.Err.Description);
            if (result.Err.Description === 'Plugin exists') {
               this.setPluginActive(device, plugin);
            } else {
               this.disconnectFromHub(device, plugin);
            }
         }
      });
   }

   private setPluginActive(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin): void {
      if (_.isNil(this.plugin)) {
         console.warn('Plantronics.Service - No plugin found.');

         return;
      }

      plugin.isActive(this.API_CONSUMER, true, (result) => {
         if (!result.isError) {
            // plugin registered and active. Show UI.
            this.isPluginRegistered$.next(true);
            this.publishDeviceList(device);
            console.log('Plantronics.Service - Plugin "' + this.API_CONSUMER + '" registered successfully.');
            this.deviceAttachedAndConnectedWithHub$.next(true);
            this.bringDeviceUpToState();
         } else {
            this.deviceAttachedAndConnectedWithHub$.next(false);
            console.debug('Plantronics.Service - Error checking if plugin is active: ' + result.Err.Description);
         }
      });
   }

   private initSDK(): void {
      this.connectToHub();
   }

   private killSdk(device: plantronicsSdk.Device, plugin: plantronicsSdk.Plugin): void {
      this.unsetPolling(); // stop polling so the automatic reconnect doesn't happen
      this.disconnectFromHub(device, plugin);
      this.spokes$.next(null);
   }

   protected initializeSdk(): void {
      this.initSDK$.next(true);
      this.killSdk$.next(false);
   }

   protected terminateSdk(): void {
      this.initSDK$.next(false);
      this.killSdk$.next(true);
   }

   // Handle events coming from headset
   private consumeHeadsetCallEvent(event) {
      const callState = plantronicsSdk.SessionCallState.Lookup[event.Action];
      switch (callState) {
         case 'AcceptCall':
            this.acceptIncomingCall();
            break;
         case 'CallInProgress':
            // nothing to do (just a confirmation after call was accepted in softphone and headset was set to active call state)
            break;
         case 'TerminateCall':
            this.endCall();
            break;
         case 'CallEnded':
            // nothing to do (is handled in the TerminateCall)
            break;
         case 'CallIdle':
            // nothing to do
            break;
         case 'HoldCall':
            this.holdCall();
            break;
         case 'Resumecall':
            this.resumeMute();
            break;
         case 'MuteON':
            // mute events are consumed as SpokesEvents even when there's no active call
            break;
         case 'MuteOFF':
            // mute events are consumed as SpokesEvents even when there's no active call
            break;
      }
   }

   private consumeHeadsetSpokesEvent(event: plantronicsSdk.SpokesEvent) {
      switch (event.Event_Name) {
         case 'Talk':
            break;
         case 'MuteOn':
            if (!this.isMuted) {
               this.toggleMute();
               this.setIsMuted(true);
            }
            break;
         case 'MuteOff':
            if (this.isMuted) {
               this.toggleMute();
               this.setIsMuted(false);
            }
            break;
         case 'VolumeUp':
            // nothing to do
            break;
         case 'VolumeDown':
            // nothing to do
            break;
      }
   }

   // ----------- Convenience functions to create call and contact structures used for IncomingCall/OutgoingCall functions ----------- //
   private getCallId(callId: number) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      return new plantronicsSdk.SpokesCallId({ Id: '' + callId });
   }

   private getContact(contactName: string) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      return new plantronicsSdk.SpokesContact({ Name: contactName });
   }
}
